"use client";
import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Shared Pagination component used across all table pages.
 *
 * Props:
 *   currentPage  – 1-based current page number
 *   totalPages   – total number of pages
 *   totalItems   – total record count (for label)
 *   pageSize     – records per page
 *   onPageChange – (page: number) => void
 *   onPageSizeChange – (size: number) => void  (optional)
 *   pageSizeOptions  – number[] (optional, default [10,25,50,100])
 */
export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  if (totalPages <= 0) return null;

  const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to   = Math.min(currentPage * pageSize, totalItems);

  // Build visible page numbers with ellipsis
  const pages = buildPageNumbers(currentPage, totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-1 py-3">
      {/* Left: record count + page size selector */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <span>
          {totalItems === 0
            ? "No records"
            : `Showing ${from}–${to} of ${totalItems} records`}
        </span>
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400">per page</span>
            <select
              value={pageSize}
              onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {pageSizeOptions.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: page buttons */}
      <div className="flex items-center gap-1">
        {/* First */}
        <NavBtn onClick={() => onPageChange(1)} disabled={currentPage === 1} title="First page">
          <ChevronsLeft className="w-3.5 h-3.5" />
        </NavBtn>
        {/* Prev */}
        <NavBtn onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} title="Previous page">
          <ChevronLeft className="w-3.5 h-3.5" />
        </NavBtn>

        {/* Page numbers */}
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className="px-2 text-gray-400 text-sm select-none">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                p === currentPage
                  ? "bg-teal-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-teal-50 hover:text-teal-700"
              }`}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <NavBtn onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} title="Next page">
          <ChevronRight className="w-3.5 h-3.5" />
        </NavBtn>
        {/* Last */}
        <NavBtn onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} title="Last page">
          <ChevronsRight className="w-3.5 h-3.5" />
        </NavBtn>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, title, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-teal-50 hover:text-teal-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );
}

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  pages.push(1);

  if (current <= 4) {
    pages.push(2, 3, 4, 5, "...", total);
  } else if (current >= total - 3) {
    pages.push("...", total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push("...", current - 1, current, current + 1, "...", total);
  }

  return pages;
}

/**
 * Hook to handle pagination state.
 * Usage: const { page, pageSize, setPage, setPageSize, paginate } = usePagination(items, 10);
 */
export function usePagination(items = [], defaultPageSize = 10) {
  const [page,     setPage]     = React.useState(1);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);

  // Reset to page 1 when items change length
  React.useEffect(() => { setPage(1); }, [items.length]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * pageSize;
  const paginated  = items.slice(start, start + pageSize);

  return {
    page: safePage,
    pageSize,
    setPage,
    setPageSize: (s) => { setPageSize(s); setPage(1); },
    totalPages,
    totalItems: items.length,
    paginated,
  };
}
